import { User } from "./../utils/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import Cookies from "js-cookie";
import { domainName } from "../utils/constants";
import { config } from "../utils/config";

const ACCESS_TOKEN_COOKIE = "jellosite-authToken";
const REFRESH_TOKEN_COOKIE = "jellosite-refreshToken";
const LEGACY_REFRESH_TOKEN_COOKIE = "jellosite-refresh-token";
const USER_COOKIE = "jellosite-user";
const IS_LOCAL_ENV = import.meta.env.VITE_ENV === "local";
type CookieAttributes = Parameters<typeof Cookies.set>[2];

const withPrimaryDomain = (options: CookieAttributes = {}): CookieAttributes =>
  IS_LOCAL_ENV ? options : { ...options, domain: `.${domainName}` };

const withStoreDomain = (options: CookieAttributes = {}): CookieAttributes =>
  IS_LOCAL_ENV ? options : { ...options, domain: `.${config.STORE_DOMAIN_NAME}` };

const storedUserInfoString = Cookies.get(USER_COOKIE);
const storedAccessToken = Cookies.get(ACCESS_TOKEN_COOKIE);
const storedRefreshToken =
  Cookies.get(REFRESH_TOKEN_COOKIE) || Cookies.get(LEGACY_REFRESH_TOKEN_COOKIE);
const storedUserInfo = storedUserInfoString
  ? JSON.parse(storedUserInfoString)
  : null;

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
}

const initialState: AuthState = {
  user: storedUserInfo || null,
  token: storedAccessToken || null,
  isAuthenticated: !!storedAccessToken,
  refreshToken: storedRefreshToken || null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User | null;
        token: string;
        refresh_token?: string;
        rememberMe?: boolean;
      }>
    ) => {
      const { user, token, refresh_token, rememberMe = false } = action.payload;
      const cookieLifetime = rememberMe ? { expires: 30 } : {};

      state.user = user;
      state.token = token;
      state.refreshToken = refresh_token || null;
      state.isAuthenticated = true;

      Cookies.set(ACCESS_TOKEN_COOKIE, token, withPrimaryDomain(cookieLifetime));
      Cookies.set(
        REFRESH_TOKEN_COOKIE,
        refresh_token || "",
        withPrimaryDomain(cookieLifetime)
      );
      // Keep writing this legacy key for compatibility with older sessions.
      Cookies.set(
        LEGACY_REFRESH_TOKEN_COOKIE,
        refresh_token || "",
        withPrimaryDomain(cookieLifetime)
      );
      if (!IS_LOCAL_ENV) {
        Cookies.set(ACCESS_TOKEN_COOKIE, token, withStoreDomain(cookieLifetime));
      }
      Cookies.set(
        USER_COOKIE,
        JSON.stringify(user || null),
        withPrimaryDomain(cookieLifetime)
      );
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      localStorage.removeItem("token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");

      if (!IS_LOCAL_ENV) {
        const domains = [`.${domainName}`, `.${config.STORE_DOMAIN_NAME}`];

        domains.forEach((domain) => {
          Cookies.remove(ACCESS_TOKEN_COOKIE, { domain });
          Cookies.remove(REFRESH_TOKEN_COOKIE, { domain });
          Cookies.remove(LEGACY_REFRESH_TOKEN_COOKIE, { domain });
          Cookies.remove(USER_COOKIE, { domain });
        });
      }

      Cookies.remove(ACCESS_TOKEN_COOKIE);
      Cookies.remove(REFRESH_TOKEN_COOKIE);
      Cookies.remove(LEGACY_REFRESH_TOKEN_COOKIE);
      Cookies.remove(USER_COOKIE);
    },
  },
});

export const { setCredentials, logout } = authSlice.actions;

export default authSlice.reducer;

// Selectors
export const selectCurrentUser = (state: { auth: AuthState }) =>
  state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
export const selectToken = (state: { auth: AuthState }) => state.auth.token;
export const selectRefreshToken = (state: { auth: AuthState }) =>
  state.auth.refreshToken;
